import React from "react";
import { CommissionProfile } from "../../../core/models/entities/Commission";
import { LogoOptions } from "../../../core/models/entities/LogoOptions";
import { Method } from "../../../core/models/entities/TransferOptions";
import {
  BaseLogo,
  getLogoColors,
  isDigital,
} from "../../../core/models/entities/BaseLogo";
import { useProfileContext } from "../../../contexts/ProfileContext";
import {
  applyCommissionAmount,
  describeCommissionRule,
  formatCommissionRate,
  resolveAdditionalFeesRule,
  resolveCommissionRule,
  resolveHeatingRule,
} from "./commission-math";
import { BoxHeaderTypography } from "../Typographies/BoxHeaderTypography";
import { DigitalColorElement } from "../Logo/DigitalColorElement";
import { VectorColorElement } from "../Logo/VectorColorElement";

export interface FullLivePreviewProps {
  logo: BaseLogo | null;
  logoOptions: LogoOptions;
  commissions: CommissionProfile;
  transfers: Method[];
  baseHeatingValue: number;
}

/** One row of the combined preview table. Either price can be missing when a
 *  quantity tier only exists in one of the two price sets (rendered as "—"). */
interface PreviewRow {
  key: string;
  label: string;
  yourPrice?: number;
  customerPrice?: number;
}

/**
 * Full live preview dialog content: the header card plus ONE combined table
 * (QTY | YOUR PRICE | CUSTOMER PAYS | MARGIN) with Extra fees and Heating as
 * section rows. Pure presentation - all numbers come from the same
 * commission-math replica of the API price engine that the inline live
 * example uses, applied to the already-adjusted NAV prices on the logo.
 */
export function FullLivePreview({
  logo,
  logoOptions,
  commissions,
  transfers,
  baseHeatingValue,
}: FullLivePreviewProps) {
  const { currency } = useProfileContext();

  if (!logo) {
    return (
      <p className="py-6 text-center text-xs italic text-gray-400">
        No example logo available
      </p>
    );
  }

  const rule = resolveCommissionRule(commissions, logo, transfers);
  const feesRule = resolveAdditionalFeesRule(commissions);
  const heatingRule = resolveHeatingRule(commissions);

  // ----- Product prices: one row per quantity tier, matched on quantity -----
  const yourByQty = new Map<number, number>();
  const customerByQty = new Map<number, number>();
  for (const price of logo.product?.prices ?? []) {
    const qty = price.minQuantity || 1;
    if (!yourByQty.has(qty)) yourByQty.set(qty, price.adjustedPrice);
    if (!customerByQty.has(qty)) {
      customerByQty.set(
        qty,
        applyCommissionAmount(price.adjustedPrice, rule.commission)
      );
    }
  }
  const quantities = Array.from(yourByQty.keys());
  Array.from(customerByQty.keys()).forEach((qty) => {
    if (quantities.indexOf(qty) === -1) quantities.push(qty);
  });
  quantities.sort((a, b) => a - b);
  const priceRows: PreviewRow[] = quantities.map((qty) => ({
    key: `qty-${qty}`,
    label: `${qty}+`,
    yourPrice: yourByQty.get(qty),
    customerPrice: customerByQty.get(qty),
  }));

  // ----- Extra fees: one row per fee bucket. Fee names are NAV data - render
  // verbatim, never translate. -----
  const feeRows: PreviewRow[] = (logo.product?.additionalFees ?? []).reduce(
    (rows, fee, feeIdx) => {
      const tiers = [...(fee.prices ?? [])].sort(
        (a, b) => a.minQuantity - b.minQuantity
      );
      tiers.forEach((price, priceIdx) => {
        rows.push({
          key: `fee-${feeIdx}-${priceIdx}`,
          label:
            tiers.length > 1 || price.minQuantity > 1
              ? `${fee.name} (${price.minQuantity || 1}+ pcs)`
              : fee.name,
          yourPrice: price.adjustedPrice,
          customerPrice: applyCommissionAmount(
            price.adjustedPrice,
            feesRule.commission
          ),
        });
      });
      return rows;
    },
    [] as PreviewRow[]
  );

  const heatingRow: PreviewRow = {
    key: "heating",
    label: "Heating",
    yourPrice: baseHeatingValue,
    customerPrice: applyCommissionAmount(
      baseHeatingValue,
      heatingRule.commission
    ),
  };

  // ----- Header card bits (same as before) -----
  const colors = getLogoColors(
    logo,
    logoOptions.colors,
    logoOptions.colorsEmbroidery
  );
  const colorsElements = isDigital(logo) ? (
    <DigitalColorElement />
  ) : (
    <div className="flex">
      {colors.map((co) =>
        co.isIndigo ? (
          <DigitalColorElement key={co.id} showText={false} text={co.name} />
        ) : (
          <VectorColorElement key={co.id} showName={false} color={co} />
        )
      )}
    </div>
  );

  return (
    // One single scroll zone (the table): title, logo card and the dialog's
    // close X stay fixed while the dialog itself never outgrows the viewport.
    <div className="flex max-h-[82vh] w-[46rem] max-w-[calc(100vw-5rem)] flex-col gap-4">
      <BoxHeaderTypography>Live preview</BoxHeaderTypography>

      {/* Header card */}
      <div className="flex items-center gap-4 rounded-md border bg-gray-150 p-3">
        <img
          src={logo.displayImage?.highResUrl || logo.displayImage?.lowResUrl}
          alt={logo.title || "Example logo"}
          className="h-16 w-16 rounded bg-white object-contain p-1"
        />
        <div className="min-w-0 text-xs text-gray-600">
          <p className="truncate font-semibold">
            {logo.title}
            {logo.product?.sku && (
              <span className="font-normal text-gray-400">
                {" "}
                ({logo.product.sku})
              </span>
            )}
          </p>
          <p className="capitalize">
            {logo.transferMethod?.type?.toLowerCase()} ·{" "}
            {logo.transferMethod?.displayName?.toLowerCase()} · {logo.heightMm}
            ×{logo.widthMm} mm
          </p>
          <div className="mt-1 flex items-center">
            <span className="pr-1 font-bold">Colors</span>
            {colorsElements}
          </div>
          <p className="mt-2">
            <span className="inline-block max-w-full rounded-full bg-[#EAF3DE] px-2.5 py-1 font-medium text-[#3B6D11]">
              Rule applied: {describeCommissionRule(rule, transfers)}
              {rule.source !== "none" &&
                ` ${formatCommissionRate(rule.commission, currency)}`}
            </span>
          </p>
        </div>
      </div>

      {/* Combined price table */}
      <div className="min-h-0 flex-1 overflow-auto rounded-md border">
        <table className="w-full table-auto">
          <thead>
            <tr>
              <HeaderCell className="pl-3 text-left">Qty</HeaderCell>
              <HeaderCell className="text-right">Your price</HeaderCell>
              <HeaderCell className="text-right">Customer pays</HeaderCell>
              <HeaderCell className="pr-3 text-right">Margin</HeaderCell>
            </tr>
          </thead>
          <tbody>
            {priceRows.map((row) => (
              <PreviewTableRow key={row.key} row={row} currency={currency} />
            ))}
            {priceRows.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-4 text-center text-xs italic text-gray-400"
                >
                  No prices set
                </td>
              </tr>
            )}

            <SectionLabelRow>Extra fees</SectionLabelRow>
            {feeRows.map((row) => (
              <PreviewTableRow key={row.key} row={row} currency={currency} />
            ))}
            {feeRows.length === 0 && (
              <tr>
                <td
                  colSpan={4}
                  className="px-3 py-2 text-xs italic text-gray-400"
                >
                  No extra fees
                </td>
              </tr>
            )}

            <SectionLabelRow>Heating</SectionLabelRow>
            <PreviewTableRow row={heatingRow} currency={currency} />
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-gray-400">
        Same math as the price engine — the most specific rule wins.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table building blocks
// ---------------------------------------------------------------------------

function HeaderCell({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <th
      className={`sticky top-0 z-10 whitespace-nowrap border-b bg-white px-2 py-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400 ${
        className ?? ""
      }`}
    >
      {children}
    </th>
  );
}

function SectionLabelRow({ children }: { children: React.ReactNode }) {
  return (
    <tr>
      <td
        colSpan={4}
        className="pb-1 pl-3 pt-4 text-[10px] font-semibold uppercase tracking-wider text-gray-400"
      >
        {children}
      </td>
    </tr>
  );
}

function PreviewTableRow({
  row,
  currency,
}: {
  row: PreviewRow;
  currency: string;
}) {
  const margin =
    row.yourPrice != null && row.customerPrice != null
      ? row.customerPrice - row.yourPrice
      : undefined;
  return (
    <tr className="border-b border-gray-100 last:border-b-0">
      <td className="py-2 pl-3 pr-2 text-xs text-gray-600">{row.label}</td>
      <AmountCell value={row.yourPrice} currency={currency} tone="muted" />
      <AmountCell
        value={row.customerPrice}
        currency={currency}
        tone="customer"
      />
      <AmountCell
        value={margin}
        currency={currency}
        tone="margin"
        className="pr-3"
      />
    </tr>
  );
}

function AmountCell({
  value,
  currency,
  tone,
  className,
}: {
  value?: number;
  currency: string;
  tone: "muted" | "customer" | "margin";
  className?: string;
}) {
  if (value == null) {
    return (
      <td
        className={`whitespace-nowrap px-2 py-2 text-right text-xs text-gray-300 ${
          className ?? ""
        }`}
      >
        —
      </td>
    );
  }
  const amountClass =
    tone === "customer"
      ? "font-medium text-gray-800"
      : tone === "margin"
      ? value < 0
        ? "text-red-600"
        : "text-emerald-700"
      : "text-gray-500";
  const amount =
    tone === "margin" && value >= 0
      ? `+${value.toFixed(2)}`
      : value.toFixed(2);
  return (
    <td
      className={`whitespace-nowrap px-2 py-2 text-right text-xs tabular-nums ${
        className ?? ""
      }`}
    >
      <span className={amountClass}>{amount}</span>{" "}
      <span className="text-gray-400">{currency}</span>
    </td>
  );
}
